type Bitmap = { width: number; height: number; pixels: ImageData }

export type EmbroideryPhotoMapping = {
	sourceWidth: number
	sourceHeight: number
	xPercent: number
	yPercent: number
	widthPercent: number
	rotationDeg: number
	reflectPhoto: boolean
}

const sourceCache = new Map<string, Promise<Bitmap>>()
const materialCache = new Map<string, Promise<string>>()
const fiberCache = new Map<string, Promise<string>>()
const photoCache = new Map<
	string,
	Promise<{ width: number; height: number; ridges: Float32Array }>
>()
const clamp = (value: number) => Math.max(0, Math.min(1, value))

function yieldToBrowser() {
	return new Promise<void>((resolve) => setTimeout(resolve, 0))
}

async function cached<T>(
	cache: Map<string, Promise<T>>,
	key: string,
	limit: number,
	create: () => Promise<T>
): Promise<T> {
	const existing = cache.get(key)
	if (existing) return existing
	const pending = create()
	cache.set(key, pending)
	if (cache.size > limit) {
		const oldest = cache.keys().next().value
		if (oldest !== undefined) cache.delete(oldest)
	}
	try {
		return await pending
	} catch (error) {
		if (cache.get(key) === pending) cache.delete(key)
		throw error
	}
}

function canvasContext(width: number, height: number) {
	const canvas = document.createElement('canvas')
	canvas.width = width
	canvas.height = height
	const context = canvas.getContext('2d', { willReadFrequently: true })
	if (!context) throw new Error('Embroidery canvas unavailable')
	return { canvas, context }
}

async function bitmap(url: string, maximum: number, square = false) {
	const image = new Image()
	image.crossOrigin = 'anonymous'
	image.src = url
	await image.decode()
	if (!image.naturalWidth || !image.naturalHeight)
		throw new Error('Embroidery image has no dimensions')
	const scale = Math.min(
		1,
		maximum / Math.max(image.naturalWidth, image.naturalHeight)
	)
	const width = Math.max(1, Math.round(image.naturalWidth * scale))
	const height = Math.max(1, Math.round(image.naturalHeight * scale))
	const edge = Math.max(width, height)
	const output = canvasContext(square ? edge : width, square ? edge : height)
	output.context.drawImage(
		image,
		(output.canvas.width - width) / 2,
		(output.canvas.height - height) / 2,
		width,
		height
	)
	return {
		width: output.canvas.width,
		height: output.canvas.height,
		pixels: output.context.getImageData(
			0,
			0,
			output.canvas.width,
			output.canvas.height
		),
	}
}

function linear(value: number) {
	return value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4
}

function srgb(value: number) {
	return (
		255 *
		(value <= 0.0031308
			? value * 12.92
			: 1.055 * value ** (1 / 2.4) - 0.055)
	)
}

function threadChannels(color: string) {
	const hex = color.replace('#', '')
	const expanded =
		hex.length === 3 ? [...hex].map((part) => part + part).join('') : hex
	if (!/^[\da-f]{6}$/i.test(expanded))
		throw new Error('Invalid embroidery thread color')
	return [0, 2, 4].map((offset) =>
		linear(parseInt(expanded.slice(offset, offset + 2), 16) / 255)
	)
}

function shadeMaterial(
	source: Bitmap,
	color: string,
	originalColors: boolean,
	rotation: number
) {
	const { width, height } = source
	const pixels = new ImageData(
		new Uint8ClampedArray(source.pixels.data),
		width,
		height
	)
	const sourceHeight = new Float32Array(width * height)
	const target = originalColors ? null : threadChannels(color)
	let peakHeight = 0
	for (let point = 0; point < sourceHeight.length; point++) {
		const i = point * 4
		const luma =
			(pixels.data[i] + pixels.data[i + 1] + pixels.data[i + 2]) / 765
		sourceHeight[point] = (pixels.data[i + 3] / 255) * luma
		peakHeight = Math.max(peakHeight, sourceHeight[point])
	}
	if (target) {
		for (let point = 0; point < sourceHeight.length; point++) {
			const i = point * 4
			const alpha = pixels.data[i + 3] / 255
			if (!alpha) continue
			// Alpha-weighted normalization ignores near-transparent white PNG fringes.
			const luma = peakHeight
				? clamp(sourceHeight[point] / alpha / peakHeight)
				: 1
			sourceHeight[point] = alpha * luma
			for (let channel = 0; channel < 3; channel++)
				pixels.data[i + channel] = srgb(target[channel] * luma)
		}
	}
	const step = Math.max(1, Math.round(width / 512))
	const angle = ((-115 - rotation) * Math.PI) / 180
	for (let y = step; y < height - step; y++) {
		for (let x = step; x < width - step; x++) {
			const point = y * width + x
			const i = point * 4
			if (!pixels.data[i + 3]) continue
			const dx =
				(sourceHeight[point + step] - sourceHeight[point - step]) * 0.45
			const dy =
				(sourceHeight[point + step * width] -
					sourceHeight[point - step * width]) *
				0.45
			const light = clamp(
				(0.8 - dx * Math.cos(angle) - dy * Math.sin(angle)) /
					Math.hypot(dx, dy, 1)
			)
			for (let channel = 0; channel < 3; channel++) {
				if (target) {
					const relief = light - 0.8
					const value = pixels.data[i + channel] * (1 + relief * 0.35)
					pixels.data[i + channel] =
						value + (255 - value) * Math.max(0, relief) * 0.08
					continue
				}
				const value = pixels.data[i + channel] * (0.86 + 0.14 * light)
				pixels.data[i + channel] =
					value + (255 - value) * light ** 8 * 0.1
			}
		}
	}
	return pixels
}

function png(pixels: ImageData) {
	const { canvas, context } = canvasContext(pixels.width, pixels.height)
	context.putImageData(pixels, 0, 0)
	return canvas.toDataURL('image/png')
}

export async function embroideryMaterial(
	url: string,
	color: string,
	originalColors: boolean,
	rotation: number
): Promise<string> {
	if (!Number.isFinite(rotation))
		throw new Error('Invalid embroidery rotation')
	return cached(
		materialCache,
		JSON.stringify([url, color, originalColors, rotation]),
		32,
		async () => {
			const source = await cached(sourceCache, url, 32, () =>
				bitmap(url, 512, true)
			)
			await yieldToBrowser()
			return png(shadeMaterial(source, color, originalColors, rotation))
		}
	)
}

function photoRidges(photo: Bitmap) {
	const { width, height } = photo
	const luma = new Float32Array(width * height)
	const ridges = new Float32Array(width * height)
	if (width < 5 || height < 5) return { width, height, ridges }
	for (let point = 0; point < luma.length; point++) {
		const i = point * 4
		luma[point] =
			(photo.pixels.data[i] * 0.2126 +
				photo.pixels.data[i + 1] * 0.7152 +
				photo.pixels.data[i + 2] * 0.0722) /
			255
	}
	const columns = new Float64Array(width)
	const columnSquares = new Float64Array(width)
	for (let y = 0; y < 5; y++) {
		for (let x = 0; x < width; x++) {
			const value = luma[y * width + x]
			columns[x] += value
			columnSquares[x] += value * value
		}
	}
	for (let y = 2; y < height - 2; y++) {
		let sum = 0
		let squares = 0
		for (let x = 0; x < 5; x++) {
			sum += columns[x]
			squares += columnSquares[x]
		}
		for (let x = 2; x < width - 2; x++) {
			const mean = sum / 25
			const deviation = Math.sqrt(Math.max(0, squares / 25 - mean * mean))
			ridges[y * width + x] = clamp(
				((luma[y * width + x] - mean) / Math.max(0.008, deviation) -
					0.25) /
					1.5
			)
			if (x + 3 < width) {
				sum += columns[x + 3] - columns[x - 2]
				squares += columnSquares[x + 3] - columnSquares[x - 2]
			}
		}
		if (y + 3 >= height) continue
		for (let x = 0; x < width; x++) {
			const leaving = luma[(y - 2) * width + x]
			const entering = luma[(y + 3) * width + x]
			columns[x] += entering - leaving
			columnSquares[x] += entering * entering - leaving * leaving
		}
	}
	return { width, height, ridges }
}

function photoPoint(u: number, v: number, mapping: EmbroideryPhotoMapping) {
	const edge = (mapping.sourceWidth * mapping.widthPercent) / 100
	const angle = (mapping.rotationDeg * Math.PI) / 180
	const dx = (u - 0.5) * edge
	const dy = (v - 0.5) * edge
	const x =
		mapping.xPercent / 100 +
		(dx * Math.cos(angle) - dy * Math.sin(angle)) / mapping.sourceWidth
	return {
		x: mapping.reflectPhoto ? 1 - x : x,
		y:
			mapping.yPercent / 100 +
			(dx * Math.sin(angle) + dy * Math.cos(angle)) /
				mapping.sourceHeight,
	}
}

export async function applyPhotoFibers(
	glyphUrl: string,
	photoUrl: string,
	mapping: EmbroideryPhotoMapping
): Promise<string> {
	const numbers = [
		mapping.sourceWidth,
		mapping.sourceHeight,
		mapping.xPercent,
		mapping.yPercent,
		mapping.widthPercent,
		mapping.rotationDeg,
	]
	if (
		!numbers.every(Number.isFinite) ||
		mapping.sourceWidth <= 0 ||
		mapping.sourceHeight <= 0 ||
		mapping.widthPercent <= 0
	)
		throw new Error('Invalid embroidery photo mapping')
	return cached(
		fiberCache,
		JSON.stringify([glyphUrl, photoUrl, mapping]),
		32,
		async () => {
			const [glyph, photo] = await Promise.all([
				bitmap(glyphUrl, 512, true),
				cached(photoCache, photoUrl, 4, async () => {
					const source = await bitmap(photoUrl, 1200)
					await yieldToBrowser()
					return photoRidges(source)
				}),
			])
			await yieldToBrowser()
			const { width, height, pixels } = glyph
			const alpha = Uint8ClampedArray.from(
				{ length: width * height },
				(_, point) => pixels.data[point * 4 + 3]
			)
			const reach = Math.max(
				1,
				Math.round(
					(2 * width * 100) /
						(mapping.sourceWidth * mapping.widthPercent)
				)
			)
			const neighbors = [
				[reach, 0],
				[-reach, 0],
				[0, reach],
				[0, -reach],
				[reach, reach],
				[-reach, reach],
				[reach, -reach],
				[-reach, -reach],
			]
			for (let y = 0; y < height; y++) {
				for (let x = 0; x < width; x++) {
					const point = y * width + x
					if (!alpha[point]) continue
					let nearest = alpha[point]
					for (const [dx, dy] of neighbors) {
						const xx = x + dx
						const yy = y + dy
						nearest = Math.min(
							nearest,
							xx < 0 || yy < 0 || xx >= width || yy >= height
								? 0
								: alpha[yy * width + xx]
						)
					}
					if (nearest === 255) continue
					const position = photoPoint(
						(x + 0.5) / width,
						(y + 0.5) / height,
						mapping
					)
					if (
						position.x < 0 ||
						position.y < 0 ||
						position.x >= 1 ||
						position.y >= 1
					)
						continue
					const ridge =
						photo.ridges[
							Math.floor(position.y * photo.height) *
								photo.width +
								Math.floor(position.x * photo.width)
						]
					pixels.data[point * 4 + 3] =
						alpha[point] * (1 - ridge * (1 - nearest / 255) * 0.24)
				}
			}
			return png(pixels)
		}
	)
}
